import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { resolveOfflineAcceptanceError } from "./offline-error";
import { OFFLINE_REASON_MAX } from "./types";
import { useRevokeOfflineAcceptance } from "./use-offline-acceptances";

export interface RevokeTarget {
  readonly id: string;
  /** Document type name (or key) shown in the confirmation. */
  readonly title: string;
}

interface RevokeOfflineAcceptanceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userId: string;
  readonly userName: string;
  readonly target: RevokeTarget | null;
  /** Lets the caller decide where focus goes once the dialog has closed. */
  readonly onCloseAutoFocus?: (event: Event) => void;
  /** Called once the revoke has succeeded, just before the dialog closes. */
  readonly onRevoked?: () => void;
}

/**
 * Confirmation + mandatory reason for revoking an offline acceptance. The row
 * is kept for audit; the person owes the active documents again from their
 * next request, which the description spells out before the call goes out.
 *
 * The form lives in its own component, mounted only while the dialog is open,
 * so every opening starts from a blank reason and no stale error.
 */
export function RevokeOfflineAcceptanceDialog({
  open,
  onOpenChange,
  userId,
  userName,
  target,
  onCloseAutoFocus,
  onRevoked,
}: RevokeOfflineAcceptanceDialogProps) {
  const mutation = useRevokeOfflineAcceptance(userId);

  if (!target) return null;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="revoke-offline-dialog" onCloseAutoFocus={onCloseAutoFocus}>
        <RevokeForm
          target={target}
          userName={userName}
          mutation={mutation}
          onCancel={() => handleOpenChange(false)}
          onDone={() => {
            onRevoked?.();
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface RevokeFormProps {
  readonly target: RevokeTarget;
  readonly userName: string;
  readonly mutation: ReturnType<typeof useRevokeOfflineAcceptance>;
  readonly onCancel: () => void;
  readonly onDone: () => void;
}

function RevokeForm({ target, userName, mutation, onCancel, onDone }: RevokeFormProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason !== "" && !mutation.isPending;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await mutation.mutateAsync({ acceptanceId: target.id, reason: trimmedReason });
      onDone();
    } catch (err) {
      setError(resolveOfflineAcceptanceError(err, t));
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <DialogHeader>
        <DialogTitle>{t("users.tos.revokeDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("users.tos.revokeDialog.description", {
            // User-chosen text: no bidi / invisible characters in the restatement.
            name: sanitizeDisplayText(userName),
            documentType: sanitizeDisplayText(target.title),
          })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2">
        <Label htmlFor="revoke-offline-reason">{t("users.tos.revokeDialog.reason")}</Label>
        <Textarea
          id="revoke-offline-reason"
          data-testid="revoke-offline-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={OFFLINE_REASON_MAX}
          disabled={mutation.isPending}
          required
          aria-describedby="revoke-offline-reason-hint"
        />
        <p id="revoke-offline-reason-hint" className="text-xs text-muted-foreground">
          {t("users.tos.revokeDialog.reasonHint")}
        </p>
      </div>

      {error && (
        <p
          role="alert"
          data-testid="revoke-offline-error"
          className="text-sm font-medium text-destructive"
        >
          {error}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          {t("users.actions.cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          data-testid="revoke-offline-confirm"
          disabled={!canSubmit}
        >
          {mutation.isPending
            ? t("users.tos.revokeDialog.pending")
            : t("users.tos.revokeDialog.confirm")}
        </Button>
      </DialogFooter>
    </form>
  );
}
